import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ApiError, api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useDocsIndex } from '../lib/docsIndex'
import { loadPage } from '../lib/page'
import { htmlToMarkdown } from '../lib/markdown'
import { toFrontmatter, type DocSource } from '../lib/frontmatter'
import { canWrite, listOf, type TreeTopic, type Visibility } from '../lib/types'
import { Button, Callout, EmptyState, Spinner, cx } from '../components/ui'

const EMPTY: DocSource = {
  service: '',
  topic: '',
  title: '',
  description: '',
  visibility: 'internal',
  tags: [],
  markdown: '',
}

/** Every topic path in a service, flattened for the datalist. */
function topicPaths(topics: TreeTopic[] | null | undefined): string[] {
  const out: string[] = []
  for (const t of listOf(topics)) {
    out.push(t.path ?? t.slug)
    out.push(...topicPaths(t.topics))
  }
  return out
}

/** Mirrors the slug the backend derives from a title, so uploads land beside the page. */
const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

/** Images referenced by a bare filename live in the doc's own Storage prefix. */
const RELATIVE_IMAGE = /!\[[^\]]*\]\((?!https?:|data:|\/)([^)\s]+)\)/g

const relativeImages = (markdown: string) =>
  [...markdown.matchAll(RELATIVE_IMAGE)].map((m) => m[1] as string)

export function EditorPage() {
  const { id } = useParams<{ id: string }>()
  const editing = id !== undefined
  const docId = Number(id)

  const navigate = useNavigate()
  const { role, loading: authLoading } = useAuth()
  const { tree } = useDocsIndex()

  const [doc, setDoc] = useState<DocSource>(EMPTY)
  const [tagsText, setTagsText] = useState('')
  const [loading, setLoading] = useState(editing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)

  // Reopen an existing page: fetch its rendered body and turn it back into Markdown.
  useEffect(() => {
    if (!editing || authLoading) return
    let cancelled = false

    ;(async () => {
      try {
        const detail = await api.doc(docId)
        const page = await loadPage(detail.page)
        if (cancelled) return

        setDoc({
          service: detail.service,
          topic: detail.topic,
          title: detail.title,
          description: detail.description,
          visibility: detail.visibility,
          tags: detail.tags,
          markdown: htmlToMarkdown(page.html),
        })
        setTagsText(detail.tags.join(', '))
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not open this document.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [editing, docId, authLoading])

  if (authLoading) return <Wrap><Spinner /></Wrap>

  if (!canWrite(role)) {
    return (
      <Wrap>
        <EmptyState title="You cannot write documentation">
          Writing requires the employee, developer, admin or root role.
        </EmptyState>
      </Wrap>
    )
  }

  if (loading) return <Wrap><Spinner label="Opening…" /></Wrap>

  const services = tree.map((s) => s.slug)
  const currentService = tree.find((s) => s.slug === doc.service)
  const paths = topicPaths(currentService?.topics)

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const tags = tagsText.split(',').map((t) => t.trim()).filter(Boolean)
      const saved = await api.saveDoc(toFrontmatter({ ...doc, tags }), {
        id: editing ? docId : undefined,
      })
      navigate(`/d/${saved?.id ?? docId}`)
    } catch (e) {
      const message =
        e instanceof ApiError && e.code === 'unknown_service'
          ? `No such service “${doc.service}”. Only an admin can create one.`
          : e instanceof Error
            ? e.message
            : 'Could not save.'
      setError(message)
      setSaving(false)
    }
  }

  /** Insert text where the caret sits, rather than appending to the end. */
  const insertAtCaret = (snippet: string) => {
    const el = bodyRef.current
    if (!el) {
      setDoc((d) => ({ ...d, markdown: `${d.markdown}\n\n${snippet}\n` }))
      return
    }
    const { selectionStart: start, selectionEnd: end, value } = el
    const next = `${value.slice(0, start)}${snippet}${value.slice(end)}`
    setDoc((d) => ({ ...d, markdown: next }))
    requestAnimationFrame(() => {
      el.focus()
      const caret = start + snippet.length
      el.setSelectionRange(caret, caret)
    })
  }

  const uploadable = editing || (doc.service && doc.topic && doc.title)

  const upload = async (files: File[]) => {
    const images = files.filter((f) => f.type.startsWith('image/'))
    if (!images.length) return

    if (!uploadable) {
      setUploadError('Fill in the service, topic and title first — the image is stored beside the page.')
      return
    }

    setUploading(true)
    setUploadError(null)
    try {
      for (const file of images) {
        // With an id the backend reads the tier off the doc; without one it needs
        // to be told where the page will live, and defaults to internal.
        const { filename } = await api.uploadImage(
          file,
          editing
            ? { docId }
            : {
                service: doc.service,
                topic: doc.topic,
                slug: slugify(doc.title),
                visibility: doc.visibility,
              },
        )
        insertAtCaret(`![${file.name.replace(/\.[^.]+$/, '')}](${filename})`)
      }
    } catch (e) {
      // `unsupported_type` covers SVG, which the backend refuses on purpose.
      setUploadError(e instanceof Error ? e.message : 'Upload failed.')
    } finally {
      setUploading(false)
    }
  }

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = [...e.clipboardData.files]
    if (files.some((f) => f.type.startsWith('image/'))) {
      e.preventDefault()
      void upload(files)
    }
  }

  const images = relativeImages(doc.markdown)
  const goingPublic = editing && doc.visibility === 'public' && images.length > 0

  const valid = doc.service && doc.topic && doc.title && doc.markdown.trim()

  return (
    <Wrap>
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          {editing ? 'Edit document' : 'New document'}
        </h1>
        {editing && (
          <Link to={`/d/${docId}`} className="text-sm text-accent-text hover:underline">
            View page
          </Link>
        )}
      </div>

      {error && (
        <div className="mt-4">
          <Callout tone="danger" title="Save failed">{error}</Callout>
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted">Service</span>
          <input
            list="services"
            value={doc.service}
            onChange={(e) => setDoc({ ...doc, service: e.target.value })}
            placeholder="core-backend-api"
            className={input}
          />
          <datalist id="services">
            {services.map((s) => <option key={s} value={s} />)}
          </datalist>
          <span className="mt-1 block text-xs text-subtle">
            Must already exist — only an admin creates services.
          </span>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted">Topic path</span>
          <input
            list="topics"
            value={doc.topic}
            onChange={(e) => setDoc({ ...doc, topic: e.target.value })}
            placeholder="doctor-desk/exams"
            className={input}
          />
          <datalist id="topics">
            {paths.map((p) => <option key={p} value={p} />)}
          </datalist>
          <span className="mt-1 block text-xs text-subtle">
            Slash path. Missing levels are created for you.
          </span>
        </label>
      </div>

      <label className="mt-4 block">
        <span className="mb-1 block text-xs font-medium text-muted">Title</span>
        <input
          value={doc.title}
          onChange={(e) => setDoc({ ...doc, title: e.target.value })}
          placeholder="PUT /api/exam_eye/reassign-doctor/{exam_id}"
          className={input}
        />
      </label>

      <label className="mt-4 block">
        <span className="mb-1 block text-xs font-medium text-muted">Description</span>
        <input
          value={doc.description}
          onChange={(e) => setDoc({ ...doc, description: e.target.value })}
          placeholder="Move an exam to a different doctor"
          className={input}
        />
      </label>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted">Tags</span>
          <input
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            placeholder="go, http, exams"
            className={cx(input, 'font-mono')}
          />
        </label>

        <fieldset>
          <legend className="mb-1 block text-xs font-medium text-muted">Visibility</legend>
          <div className="flex gap-2">
            {(['internal', 'public'] as Visibility[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setDoc({ ...doc, visibility: v })}
                className={cx(
                  'h-9 flex-1 rounded-[var(--radius-sh)] border text-sm font-medium transition-colors',
                  doc.visibility === v
                    ? 'border-accent bg-accent-soft text-accent-text'
                    : 'border-line bg-surface text-muted hover:text-ink',
                )}
              >
                {v}
              </button>
            ))}
          </div>
          <span className="mt-1 block text-xs text-subtle">
            {doc.visibility === 'internal'
              ? 'Staff only. Stored under internal/ and gated by Storage rules.'
              : 'Readable by anyone, signed in or not.'}
          </span>
        </fieldset>
      </div>

      <div className="mt-6">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-medium text-muted">Markdown</span>
          <label className="cursor-pointer text-xs font-medium text-accent-text hover:underline">
            {uploading ? 'Uploading…' : 'Attach image'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              multiple
              hidden
              disabled={uploading}
              onChange={(e) => {
                void upload([...(e.target.files ?? [])])
                e.target.value = ''
              }}
            />
          </label>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            void upload([...e.dataTransfer.files])
          }}
          className={cx(
            'rounded-[var(--radius-sh)] transition-shadow',
            dragging && 'ring-2 ring-accent',
          )}
        >
          <textarea
            ref={bodyRef}
            value={doc.markdown}
            onChange={(e) => setDoc({ ...doc, markdown: e.target.value })}
            onPaste={onPaste}
            rows={20}
            spellCheck={false}
            placeholder={'## Request\n\n```json\n{ "sku": "000/290" }\n```'}
            className={cx(input, 'h-auto resize-y py-2 font-mono text-[13px] leading-relaxed')}
          />
        </div>

        <p className="mt-1 text-xs text-subtle">
          Drop a screenshot here, or paste one from the clipboard. It is stored beside the page,
          in the same tier — an internal page keeps its images internal. SVG is refused.
        </p>
        {uploadError && <p className="mt-1 text-xs text-danger">{uploadError}</p>}
      </div>

      {goingPublic && (
        <div className="mt-4">
          <Callout tone="warn" title="These images will become public">
            This page carries {images.length} image{images.length === 1 ? '' : 's'} (
            <span className="font-mono">{images.join(', ')}</span>). Publishing moves them out of
            the internal tier, and anyone will be able to open them.
          </Callout>
        </div>
      )}

      <div className="mt-6 flex items-center gap-3">
        <Button variant="primary" disabled={!valid || saving} onClick={() => void save()}>
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Publish'}
        </Button>
        <Button variant="ghost" disabled={saving} onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <span className="text-xs text-subtle">
          The backend renders the page and republishes the search index.
        </span>
      </div>
    </Wrap>
  )
}

const input =
  'h-9 w-full rounded-[var(--radius-sh)] border border-line bg-surface px-3 text-sm text-ink placeholder:text-subtle focus:border-accent focus:outline-none'

const Wrap = ({ children }: { children: React.ReactNode }) => (
  <div className="mx-auto max-w-3xl px-1 py-10 lg:px-8">{children}</div>
)
